/**
 * Sentry initialisation for apps/admin.
 *
 * Per CLAUDE.md "Frontend (Sparta)" rule §14, no PII (guest names,
 * owner financials, addresses, reservation details, AI prompts, or
 * tool outputs) ever leaves the browser through Sentry. The
 * `beforeSend` hook below scrubs every surface the Sentry event
 * envelope exposes:
 *
 *   - `event.request.url`   — search params stripped.
 *   - `event.request.headers` / `cookies` / `data` (body)
 *     — recursively redacted on sensitive keys.
 *   - `event.user`          — recursively redacted.
 *   - `event.exception.values[].value` — message strings get a
 *     simple value-side redaction so accidental token / email /
 *     credit-card-shaped substrings don't ride along in the
 *     exception text.
 *   - `event.breadcrumbs[].message` and `.data` — same treatment.
 *   - `event.contexts` / `event.extra` / `event.tags` — same.
 *
 * If `VITE_SENTRY_DSN` is empty the SDK is not initialised at all.
 * `Sentry.captureException` then becomes a no-op via the SDK's
 * safe-by-default behaviour, so feature code can call into Sentry
 * unconditionally.
 */

import * as Sentry from '@sentry/react';
import { env } from '../shared/env.js';

// Sensitive key fragments. ALL stored lowercased so the
// comparison against `key.toLowerCase()` works for camelCase keys
// like `aiPrompt` / `firstName` (codex I2 case-bug fix).
const SENSITIVE_KEY_FRAGMENTS: readonly string[] = [
  // identity (note: bare 'name' is too broad — it matches `username` /
  // `filename` / `displayName`. We instead rely on the
  // domain-specific 'guest' / 'owner' fragments below to catch
  // `guestName` / `ownerName`, plus value-side patterns to catch
  // email / id / number-sequence PII on strings.)
  'firstname',
  'lastname',
  'fullname',
  'email',
  'phone',
  'address',
  // session / auth
  'token',
  'password',
  'secret',
  'authorization',
  'cookie',
  'session',
  'csrf',
  // organization / business
  'guest',
  'owner',
  // reservation / billing
  'reservation',
  'booking',
  'financial',
  'payment',
  'tax',
  'invoice',
  'card',
  // ai surfaces
  'aiprompt',
  'airesponse',
  'tooloutput',
  'completion',
];

// Value-side redaction patterns — applied to free-text strings such
// as exception messages and breadcrumb messages. Catches stray PII
// that didn't ride a key.
const VALUE_PATTERNS: readonly { regex: RegExp; replacement: string }[] = [
  // email-like
  { regex: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, replacement: '[REDACTED-EMAIL]' },
  // bearer / token / authorization values (long opaque strings)
  { regex: /(bearer|token|secret|api[_-]?key)\s*[=:]\s*[A-Z0-9._-]{12,}/gi, replacement: '$1=[REDACTED]' },
  // 13-19 digit number sequences (likely card / phone / id)
  { regex: /\b\d{13,19}\b/g, replacement: '[REDACTED-NUMSEQ]' },
];

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((frag) => lower.includes(frag));
}

function redactString(input: string): string {
  let out = input;
  for (const { regex, replacement } of VALUE_PATTERNS) {
    out = out.replace(regex, replacement);
  }
  return out;
}

export function redactValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return redactString(value);
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redactValue(v));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (isSensitiveKey(k)) {
      out[k] = '[REDACTED]';
      continue;
    }
    out[k] = redactValue(v);
  }
  return out;
}

function stripSearchParams(url: string): string {
  try {
    const u = new URL(url);
    u.search = '';
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Apply the redactor to every PII-bearing field on a Sentry event.
 * Exported so unit tests can assert the contract directly.
 */
export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  // Request surface
  if (event.request) {
    if (event.request.url) {
      event.request.url = stripSearchParams(event.request.url);
    }
    if (event.request.headers) {
      event.request.headers = redactValue(event.request.headers) as Record<string, string>;
    }
    if (event.request.cookies) {
      event.request.cookies = redactValue(event.request.cookies) as typeof event.request.cookies;
    }
    if (event.request.data !== undefined) {
      event.request.data = redactValue(event.request.data);
    }
    if (event.request.query_string) {
      event.request.query_string = '[REDACTED]';
    }
  }

  // User surface
  if (event.user) {
    event.user = redactValue(event.user) as typeof event.user;
  }

  // Exception messages
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: typeof ex.value === 'string' ? redactString(ex.value) : ex.value,
    }));
  }

  // Top-level message
  if (typeof event.message === 'string') {
    event.message = redactString(event.message);
  }

  // Breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: typeof b.message === 'string' ? redactString(b.message) : b.message,
      data: b.data ? (redactValue(b.data) as Record<string, unknown>) : b.data,
    }));
  }

  // Extras / contexts / tags
  if (event.extra) {
    event.extra = redactValue(event.extra) as Record<string, unknown>;
  }
  if (event.contexts) {
    event.contexts = redactValue(event.contexts) as typeof event.contexts;
  }
  if (event.tags) {
    event.tags = redactValue(event.tags) as typeof event.tags;
  }

  return event;
}

export function initSentry(): void {
  if (!env.sentryDsn) {
    return;
  }
  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.sentryEnvironment,
    release: env.buildVersion,
    tracesSampleRate: env.sentryEnvironment === 'production' ? 0.1 : 0,
    integrations: [],
    beforeSend(event) {
      return scrubEvent(event);
    },
  });
}
